import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate, todayISO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, Download } from "lucide-react";
import * as XLSX from "xlsx";

const currentMonth = () => todayISO().slice(0, 7);

export default function OperationalExpense() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: todayISO(),
    category: "Listrik",
    description: "",
    amount: 0,
    payment_method: "cash",
    reference_no: "",
    notes: "",
  });

  const load = () => Promise.all([
    api.get(`/operational-expenses?month=${month}`).then((r) => setItems(r.data)),
    api.get("/operational-expenses/categories").then((r) => setCategories(r.data.categories)),
  ]);
  useEffect(() => { load(); }, [month]);

  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const byCategory = items.reduce((acc, i) => {
    acc[i.category] = (acc[i.category] || 0) + Number(i.amount || 0);
    return acc;
  }, {});

  const save = async () => {
    if (!form.description.trim()) return toast.error("Deskripsi wajib diisi");
    if (Number(form.amount) <= 0) return toast.error("Jumlah harus > 0");
    try {
      await api.post("/operational-expenses", { ...form, amount: Number(form.amount) });
      toast.success("Beban tercatat — jurnal otomatis diposting");
      setOpen(false);
      setForm({ date: todayISO(), category: "Listrik", description: "", amount: 0, payment_method: "cash", reference_no: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus beban ini? Jurnal & arus kas juga akan di-reverse.")) return;
    try { await api.delete(`/operational-expenses/${id}`); toast.success("Beban dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const exportXLSX = () => {
    const rows = items.map((i) => ({
      "No. Bukti": i.expense_number,
      "Tanggal": i.date,
      "Kategori": i.category,
      "Deskripsi": i.description,
      "Metode": i.payment_method === "cash" ? "Kas" : "Bank",
      "Ref": i.reference_no,
      "Jumlah": i.amount,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BebanOperasional");
    XLSX.writeFile(wb, `Beban_${month}.xlsx`);
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Keuangan</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Beban Operasional</h1>
          <p className="text-sm text-slate-500 mt-1">Input biaya listrik, entertain, sewa, ATK, dll — otomatis terjurnalkan ke akun 6000 (Beban Operasional) dan arus kas keluar.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Periode</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="expense-month-filter" />
          </div>
          <Button variant="outline" onClick={exportXLSX} data-testid="expense-export"><Download size={14} className="mr-1" />Excel</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="expense-new-btn"><Plus size={16} className="mr-2" />Input Beban</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-display">Input Beban Operasional</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></div>
                <div>
                  <Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Deskripsi</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Cth: PLN Januari 2026" data-testid="expense-description" /></div>
                <div><Label>Jumlah (Rp)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></div>
                <div>
                  <Label>Metode Bayar</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger data-testid="expense-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Kas Tunai</SelectItem>
                      <SelectItem value="bank">Transfer Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>No. Bukti / Nota</Label><Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="Opsional" data-testid="expense-ref" /></div>
                <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="expense-save">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="card-elevated col-span-1 md:col-span-1">
          <CardContent className="p-4 flex items-center gap-3">
            <Wallet className="text-red-500" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Beban {month}</div>
              <div className="font-display text-xl font-extrabold text-red-700" data-testid="expense-total">{formatIDR(total)}</div>
              <div className="text-xs text-slate-500">{items.length} transaksi</div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated col-span-1 md:col-span-3">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Breakdown per Kategori</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                <Badge key={cat} className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100 font-normal px-3 py-1">
                  {cat} <span className="font-mono ml-2 text-navy-900 font-bold">{formatIDR(amt)}</span>
                </Badge>
              ))}
              {Object.keys(byCategory).length === 0 && <div className="text-xs text-slate-400">Belum ada beban tercatat bulan ini</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-elevated"><CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="expense-table">
            <TableHeader>
              <TableRow>
                <TableHead>No. Bukti</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Deskripsi</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Ref</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`expense-row-${i.expense_number}`}>
                  <TableCell className="font-mono text-xs font-semibold">{i.expense_number}</TableCell>
                  <TableCell>{formatDate(i.date)}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100">{i.category}</Badge></TableCell>
                  <TableCell>{i.description}</TableCell>
                  <TableCell>{i.payment_method === "cash" ? "Kas" : "Bank"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{i.reference_no || "-"}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-red-700">{formatIDR(i.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(i.id)} data-testid={`expense-delete-${i.expense_number}`}><Trash2 size={14} className="text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Belum ada beban bulan ini</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}
